/**
 * LinkedIn inbox workspace: master/detail (and mobile stepper) for chats, composer, contact pane, AI helper.
 *
 * Where: `/inbox` route.
 * Uses: `chatApi`, `MessageList`, `ConversationView`, `ContactDetails`, `MobileAppBar`, `AiHelperFloatingButton`.
 * Behavior: URL-driven chat selection; infinite chat loading; handles send/schedule flows and email notification modal.
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient, InfiniteData } from '@tanstack/react-query';
import { chatApi } from '@/api/chat';
import { useAuth } from '@/hooks/useAuth';
import type { Chat, ChatFilters } from '@/types/chat';
import { MessageList, ConversationView, ContactDetails, MobileAppBar, EmailNotificationForm } from '@/components/Inbox';
import { useInboxPendingSend } from '@/components/Inbox/InboxPendingSendContext';
import { AiHelperFloatingButton } from '@/components/AiHelper';
import type { ChatResponse } from '@/types/chat';

interface InboxProps {}

interface FilterState {
  search: string;
  readStatus: 'all' | 'messages' | 'unread' | 'read' | 'archived' | 'important' | 'last_receiver';
  streams: string[];
  dateFrom: Date | null;
  dateTo: Date | null;
  messageCategories: string[];
  conversationSignals: string[];
  conversationState: string | null;
  owners: string[];
  tags: string[];
  lastSenders: string[];
}

interface SignalFilterOption {
  value: string;
  label: string;
  description: string;
  dot: string;
  bg: string;
  text: string;
  border: string;
}

export const Inbox: React.FC<InboxProps> = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [view, setView] = useState<'list' | 'conversation' | 'details'>('list');
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    readStatus: 'all',
    streams: [],
    dateFrom: null,
    dateTo: null,
    messageCategories: [],
    conversationSignals: [],
    conversationState: null,
    owners: [],
    tags: [],
    lastSenders: [],
  });
  const [expandedFilters, setExpandedFilters] = useState(false);
  
  // Initialize activeTab from URL params, default to 0
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam !== null) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 6) {
        return tabIndex;
      }
    }
    return 0;
  });
  
  // Track if we're updating from internal state to avoid circular updates
  const isInternalUpdateRef = useRef(false);
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [emailNotificationOpen, setEmailNotificationOpen] = useState(false);
  
  const queryClient = useQueryClient();
  const { setSendSuccessHandler } = useInboxPendingSend();
  const selectedChatRef = useRef(selectedChat);
  selectedChatRef.current = selectedChat;
  const updateChatInListCaches = useCallback(
    (chatId: string, updater: (chat: Chat) => Chat) => {
      queryClient.setQueriesData<InfiniteData<ChatResponse>>(
        { queryKey: ['chats'] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              output: {
                ...page.output,
                items: page.output.items.map((chat) =>
                  chat.key === chatId ? updater(chat) : chat
                ),
              },
            })),
          };
        }
      );
    },
    [queryClient]
  );

  useEffect(() => {
    setSendSuccessHandler((updatedChat, chatId) => {
      if (updatedChat && selectedChatRef.current?.key === chatId) {
        setSelectedChat(updatedChat);
      }
    });
    return () => setSendSuccessHandler(null);
  }, [setSendSuccessHandler]);

  // Get prospect or chat_uuid from URL params
  const prospectParam = searchParams.get('prospect');
  const chatUuidParam = searchParams.get('chat_uuid');

  // Convert filters to API format
  const apiFilters: ChatFilters = {
    search: filters.search || undefined,
    read_status: filters.readStatus === 'unread' ? 'unseen' : 
                 filters.readStatus === 'read' ? 'seen' : undefined,
    stream: filters.streams.length > 0 ? filters.streams : undefined, // Changed from campaigns to streams
    message_categories: filters.messageCategories.length > 0 ? filters.messageCategories : undefined,
    conversation_signals: filters.conversationSignals.length > 0 ? filters.conversationSignals : undefined,
    conversation_state: filters.conversationState || undefined,
    tags: filters.tags.length > 0 ? filters.tags : undefined,
    operator: filters.owners.length > 0 ? filters.owners : undefined,
    last_sender: filters.lastSenders.length > 0 ? filters.lastSenders : undefined,
    date_from: filters.dateFrom ? filters.dateFrom.toISOString().split('T')[0] : undefined,
    date_to: filters.dateTo ? filters.dateTo.toISOString().split('T')[0] : undefined,
    // Only send is_archived/is_important/is_last_receiver when those specific categories are selected
    // and clear read_status to avoid conflicts
    ...(filters.readStatus === 'archived' && { 
      is_archived: true,
      read_status: undefined 
    }),
    ...(filters.readStatus === 'important' && { 
      is_important: true,
      read_status: undefined 
    }),
    ...(filters.readStatus === 'last_receiver' && { 
      is_last_receiver: true,
      read_status: undefined 
    }),
    ...(filters.readStatus === 'messages' && {
      exclude_pipeline_last_sender: true,
      read_status: undefined,
    }),
    // Add prospect or chat filter from URL params
    ...(prospectParam ? { prospect: prospectParam } : {}),
    ...(chatUuidParam ? { chat_uuid: chatUuidParam } : {}),
  };

  // Fetch chats with infinite scroll
  const {
    data: chatsPages,
    isLoading: isLoadingChats,
    error: chatsError,
    fetchNextPage: fetchNextChatsPage,
    hasNextPage: hasNextChatsPage,
    isFetchingNextPage: isFetchingNextChatsPage,
  } = useInfiniteQuery({
    queryKey: ['chats', apiFilters],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      chatApi.getChats({ page_size: 50, page_token: pageParam ?? undefined, ...apiFilters }),
    getNextPageParam: (lastPage: ChatResponse) => lastPage?.output?.next_page_token ?? undefined,
    initialPageParam: undefined as string | undefined,
  });

  // Auto-select chat if chat_uuid is provided
  useEffect(() => {
    if (chatUuidParam && chatsPages && !selectedChat) {
      const allChats = chatsPages.pages.flatMap((p: ChatResponse) => p.output.items);
      const targetChat = allChats.find((c: Chat) => c.key === chatUuidParam);
      if (targetChat) {
        setSelectedChat(targetChat);
        if (isMobile) {
          setView('conversation');
        }
      }
    }
  }, [chatUuidParam, chatsPages, selectedChat, isMobile]);

  // Fetch filter options - replaced campaigns with streams
  // Include user key in query key to ensure proper data refresh on impersonation
  const { data: streams = [] } = useQuery({
    queryKey: ['streams', user?.key],
    queryFn: chatApi.getStreams,
  });

  const { data: messageCategories = [] } = useQuery({
    queryKey: ['messageCategories'],
    queryFn: chatApi.getMessageCategories,
  });

  const {
    data: conversationSignalPages,
    fetchNextPage: fetchNextConversationSignalPage,
    hasNextPage: hasNextConversationSignalPage,
    isFetchingNextPage: isFetchingNextConversationSignalPage,
  } = useInfiniteQuery({
    queryKey: ['inbox-conversation-signal-options', user?.key],
    enabled: !!user,
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      chatApi.getAccessibleConversationSignals({
        page_token: pageParam,
        max_results: 100,
        is_active: true,
      }),
    getNextPageParam: (lastPage) => lastPage.next_page_token ?? undefined,
    initialPageParam: undefined as string | undefined,
  });

  const conversationSignalOptions = useMemo<SignalFilterOption[]>(() => {
    const signalPages = conversationSignalPages?.pages ?? [];
    const mergedByLabel = new Map<
      string,
      { priority: number; option: SignalFilterOption }
    >();

    const getScopePriority = (scopeType: string | null): number => {
      if (scopeType === 'stream') return 3;
      if (scopeType === 'user') return 2;
      return 1;
    };

    for (const page of signalPages) {
      for (const signal of page.items ?? []) {
        const nextOption: SignalFilterOption = {
          value: signal.label,
          label: signal.label,
          description: signal.description || '',
          dot: signal.filter_colors?.['dot'] || '#9e9e9e',
          bg: signal.filter_colors?.['bg'] || '#f5f5f5',
          text: signal.filter_colors?.['text'] || '#424242',
          border: signal.filter_colors?.['border'] || '#e0e0e0',
        };
        const priority = getScopePriority(signal.scope_type);
        const existing = mergedByLabel.get(signal.label);
        if (!existing || priority > existing.priority) {
          mergedByLabel.set(signal.label, { priority, option: nextOption });
        }
      }
    }

    return Array.from(mergedByLabel.values())
      .map((entry) => entry.option)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [conversationSignalPages]);

  useEffect(() => {
    if (hasNextConversationSignalPage && !isFetchingNextConversationSignalPage && user) {
      fetchNextConversationSignalPage();
    }
  }, [
    hasNextConversationSignalPage,
    isFetchingNextConversationSignalPage,
    fetchNextConversationSignalPage,
    user,
  ]);

  // Owners (users current user has access to for chats) - using infinite query to load all
  const {
    data: ownersPages,
    fetchNextPage: fetchNextOwnersPage,
    hasNextPage: hasNextOwnersPage,
    isFetchingNextPage: isFetchingNextOwnersPage,
  } = useInfiniteQuery({
    queryKey: ['chatOwners', user?.key],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      chatApi.getProjectOwners({ page_token: pageParam, max_results: 50 }),
    getNextPageParam: (lastPage) => lastPage.next_page_token ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: !!user,
  });

  // Flatten all owners from all pages
  const owners = (ownersPages?.pages.flatMap((p) => p.items) ?? []) as { uuid: string; name: string }[];

  // Load more owners when needed (auto-load all pages)
  useEffect(() => {
    if (hasNextOwnersPage && !isFetchingNextOwnersPage && user) {
      fetchNextOwnersPage();
    }
  }, [hasNextOwnersPage, isFetchingNextOwnersPage, fetchNextOwnersPage, user]);

  // Schedule message mutation
  const scheduleMessageMutation = useMutation({
    mutationFn: ({ chatId, content, scheduledAt }: { chatId: string; content: string; scheduledAt: Date }) =>
      chatApi.scheduleMessage(chatId, content, scheduledAt.toISOString()),
    onSuccess: (_, { chatId }) => {
      queryClient.invalidateQueries({ queryKey: ['scheduledMessages', chatId] });
      queryClient.invalidateQueries({ queryKey: ['chats'] });
      setToast({
        open: true,
        message: 'Message scheduled successfully!',
        severity: 'success',
      });
    },
    onError: (error) => {
      console.error('Failed to schedule message:', error);
      setToast({
        open: true,
        message: 'Failed to schedule message. Please try again.',
        severity: 'error',
      });
    },
  });

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: (chatId: string) => chatApi.markAsSeen(chatId),
    onMutate: async (chatId: string) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['chats'] });

      // Snapshot the previous value
      const previousChats = queryClient.getQueryData(['chats', apiFilters]);

      // Optimistically update the chat in the list
      queryClient.setQueryData(['chats', apiFilters], (old: any) => {
        if (!old) return old;
        
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            output: {
              ...page.output,
              items: page.output.items.map((chat: Chat) => 
                chat.key === chatId 
                  ? { ...chat, seen_last_message: true }
                  : chat
              )
            }
          }))
        };
      });

      // Return a context object with the snapshotted value
      return { previousChats };
    },
    onError: (_err, _chatId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousChats) {
        queryClient.setQueryData(['chats', apiFilters], context.previousChats);
      }
    },
    onSuccess: (updatedChat, chatId) => {
      updateChatInListCaches(chatId, () => updatedChat);
      if (selectedChatRef.current?.key === chatId) {
        setSelectedChat(updatedChat);
      }
    },
  });

  // Mark as important mutation
  const markAsImportantMutation = useMutation({
    mutationFn: ({ chatId, isImportant }: { chatId: string; isImportant: boolean }) => 
      chatApi.markAsImportant(chatId, isImportant),
    onMutate: async ({ chatId, isImportant }) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['chats'] });

      // Snapshot the previous value
      const previousChats = queryClient.getQueryData(['chats', apiFilters]);

      // Optimistically update the chat in the list
      queryClient.setQueryData(['chats', apiFilters], (old: any) => {
        if (!old) return old;
        
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            output: {
              ...page.output,
              items: page.output.items.map((chat: Chat) => 
                chat.key === chatId 
                  ? { ...chat, is_important: isImportant }
                  : chat
              )
            }
          }))
        };
      });

      // Return a context object with the snapshotted value
      return { previousChats };
    },
    onError: (_err, _variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousChats) {
        queryClient.setQueryData(['chats', apiFilters], context.previousChats);
      }
    },
    onSuccess: (updatedChat, { chatId }) => {
      updateChatInListCaches(chatId, () => updatedChat);
      if (selectedChatRef.current?.key === chatId) {
        setSelectedChat(updatedChat);
      }
    },
  });

  // Mark as archived mutation
  const markAsArchivedMutation = useMutation({
    mutationFn: ({ chatId, isArchived }: { chatId: string; isArchived: boolean }) => 
      chatApi.markAsArchived(chatId, isArchived),
    onMutate: async ({ chatId, isArchived }) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['chats'] });

      // Snapshot the previous value
      const previousChats = queryClient.getQueryData(['chats', apiFilters]);

      // Optimistically update the chat in the list
      queryClient.setQueryData(['chats', apiFilters], (old: any) => {
        if (!old) return old;
        
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            output: {
              ...page.output,
              items: page.output.items.map((chat: Chat) => 
                chat.key === chatId 
                  ? { ...chat, is_archived: isArchived }
                  : chat
              )
            }
          }))
        };
      });

      // Return a context object with the snapshotted value
      return { previousChats };
    },
    onError: (_err, _variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousChats) {
        queryClient.setQueryData(['chats', apiFilters], context.previousChats);
      }
    },
    onSuccess: (updatedChat, { chatId }) => {
      updateChatInListCaches(chatId, () => updatedChat);
      if (selectedChatRef.current?.key === chatId) {
        setSelectedChat(updatedChat);
      }
    },
  });

  // Mark as unread mutation
  const markAsUnreadMutation = useMutation({
    mutationFn: (chatId: string) => chatApi.markAsUnread(chatId),
    onMutate: async (chatId: string) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['chats'] });

      // Snapshot the previous value
      const previousChats = queryClient.getQueryData(['chats', apiFilters]);

      // Optimistically update the chat in the list
      queryClient.setQueryData(['chats', apiFilters], (old: any) => {
        if (!old) return old;
        
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            output: {
              ...page.output,
              items: page.output.items.map((chat: Chat) => 
                chat.key === chatId 
                  ? { ...chat, seen_last_message: false }
                  : chat
              )
            }
          }))
        };
      });

      // Return a context object with the snapshotted value
      return { previousChats };
    },
    onError: (_err, _chatId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousChats) {
        queryClient.setQueryData(['chats', apiFilters], context.previousChats);
      }
    },
    onSuccess: (updatedChat, chatId) => {
      updateChatInListCaches(chatId, () => updatedChat);
      if (selectedChatRef.current?.key === chatId) {
        setSelectedChat(updatedChat);
      }
    },
  });

  const chats = (chatsPages as InfiniteData<ChatResponse> | undefined)?.pages.flatMap((p) => p.output.items) || [];

  // Handle chat selection
  const handleChatSelect = useCallback((chat: Chat) => {
    // If the chat is unread, optimistically update it as read immediately
    if (!chat.seen_last_message) {
      const updatedChat = { ...chat, seen_last_message: true };
      setSelectedChat(updatedChat);
      markAsReadMutation.mutate(chat.key);
    } else {
      setSelectedChat(chat);
    }
    
    if (isMobile) {
      setView('conversation');
    }
  }, [isMobile, markAsReadMutation]);

  // Handle schedule message
  const handleScheduleMessage = useCallback((message: string, scheduledAt: Date) => {
    if (selectedChat && !scheduleMessageMutation.isPending) {
      scheduleMessageMutation.mutate({
        chatId: selectedChat.key,
        content: message,
        scheduledAt,
      });
    }
  }, [selectedChat, scheduleMessageMutation]);

  // Handle mark as important
  const handleMarkImportant = useCallback((chatId: string, isImportant: boolean) => {
    markAsImportantMutation.mutate({ chatId, isImportant });
  }, [markAsImportantMutation]);

  // Handle mark as archived
  const handleMarkArchived = useCallback((chatId: string, isArchived: boolean) => {
    markAsArchivedMutation.mutate({ chatId, isArchived });
  }, [markAsArchivedMutation]);

  // Handle mark as unread
  const handleMarkUnread = useCallback((chatId: string) => {
    markAsUnreadMutation.mutate(chatId);
  }, [markAsUnreadMutation]);

  // Handle filter changes
  const handleFilterChange = (key: keyof FilterState, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Update URL when tab changes
  const updateURLTab = useCallback((tabIndex: number) => {
    isInternalUpdateRef.current = true;
    const newSearchParams = new URLSearchParams(searchParams);
    
    if (tabIndex === 0) {
      // Remove tab param if it's the default (first tab)
      newSearchParams.delete('tab');
    } else {
      newSearchParams.set('tab', tabIndex.toString());
    }
    
    setSearchParams(newSearchParams, { replace: true });
    // Reset flag after a short delay to allow URL to update
    setTimeout(() => {
      isInternalUpdateRef.current = false;
    }, 0);
  }, [searchParams, setSearchParams]);

  // Sync state from URL params when URL changes externally (e.g., browser back button)
  useEffect(() => {
    if (isInternalUpdateRef.current) {
      return; // Skip if this is from our own internal update
    }

    const tabParam = searchParams.get('tab');
    let urlTabIndex = 0;
    if (tabParam !== null) {
      const parsedTab = parseInt(tabParam, 10);
      if (!isNaN(parsedTab) && parsedTab >= 0 && parsedTab <= 6) {
        urlTabIndex = parsedTab;
      }
    }

    if (urlTabIndex !== activeTab) {
      setActiveTab(urlTabIndex);
      // Also update the filter status to match the tab
      const statusMap = ['all', 'messages', 'last_receiver', 'unread', 'read', 'important', 'archived'];
      setFilters(prev => ({ ...prev, readStatus: statusMap[urlTabIndex] as any }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Handle tab change
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    const statusMap = ['all', 'messages', 'last_receiver', 'unread', 'read', 'important', 'archived'];
    handleFilterChange('readStatus', statusMap[newValue] as any);
    updateURLTab(newValue);
  };

  // Mobile navigation
  const handleBack = () => {
    if (view === 'conversation') {
      setView('list');
      setSelectedChat(null);
    } else if (view === 'details') {
      setView('conversation');
    }
  };

  const handleNavigateToDetails = () => {
    if (isMobile) {
      setView('details');
    }
  };

  // Handle email notification
  const handleSendEmailNotification = () => {
    setEmailNotificationOpen(true);
  };

  const handleCloseEmailNotification = () => {
    setEmailNotificationOpen(false);
  };

  // Check if user is admin
  const isAdmin = user?.is_staff || user?.is_superuser;



  return (
    <Box sx={{ height: '100%', maxHeight: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 0, minHeight: 0 }}>
      {/* Mobile App Bar */}
      {isMobile && (
        <MobileAppBar
          view={view}
          selectedChat={selectedChat}
          onBack={handleBack}
        />
      )}

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
        {/* Desktop Layout */}
        {!isMobile && (
          <>
            {/* Left Section */}
            <Box sx={{ width: 380, borderRight: 1, borderColor: 'divider', height: '100%', maxHeight: '100%', overflow: 'hidden', minHeight: 0, flexShrink: 0 }}>
              <MessageList
                filters={filters}
                onFilterChange={handleFilterChange}
                selectedChat={selectedChat}
                onChatSelect={handleChatSelect}
                chats={chats}
                isLoading={isLoadingChats}
                error={chatsError}
                fetchNextPage={fetchNextChatsPage}
                hasNextPage={hasNextChatsPage}
                isFetchingNextPage={isFetchingNextChatsPage}
                streams={streams}
                owners={owners}
                messageCategories={messageCategories}
                conversationSignalOptions={conversationSignalOptions}
                expandedFilters={expandedFilters}
                onExpandedFiltersChange={setExpandedFilters}
                activeTab={activeTab}
                onTabChange={handleTabChange}
              />
            </Box>

            {/* Middle Section */}
            <Box sx={{ flex: 1, minWidth: 0, borderRight: isTablet ? 0 : 1, borderColor: 'divider', height: '100%', maxHeight: '100%', overflow: 'hidden', minHeight: 0 }}>
              <ConversationView
                selectedChat={selectedChat}
                onScheduleMessage={handleScheduleMessage}
                onMarkImportant={handleMarkImportant}
                onMarkArchived={handleMarkArchived}
                onMarkUnread={handleMarkUnread}
                onNavigateToDetails={handleNavigateToDetails}
                onSendEmailNotification={handleSendEmailNotification}
                isAdmin={isAdmin}
                owners={owners}
                onChatUpdate={(updatedChat) => {
                  setSelectedChat(updatedChat);
                  queryClient.invalidateQueries({ queryKey: ['chats'] });
                }}
                onSendScheduledNowSuccess={() => {
                  setToast({ open: true, message: 'Message sent!', severity: 'success' });
                }}
                onSendScheduledNowError={() => {
                  setToast({ open: true, message: 'Failed to send message', severity: 'error' });
                }}
              />
            </Box>

            {/* Right Section */}
            {!isTablet && (
              <Box sx={{ width: 380, height: '100%', maxHeight: '100%', overflow: 'hidden', minHeight: 0, flexShrink: 0 }}>
                <ContactDetails selectedChat={selectedChat} />
              </Box>
            )}
          </>
        )}

        {/* Mobile Layout */}
        {isMobile && (
          <Box sx={{ flex: 1, height: '100%', maxHeight: '100%', overflow: 'hidden', minHeight: 0 }}>
            {view === 'list' && (
              <MessageList
                filters={filters}
                onFilterChange={handleFilterChange}
                selectedChat={selectedChat}
                onChatSelect={handleChatSelect}
                chats={chats}
                isLoading={isLoadingChats}
                error={chatsError}
                fetchNextPage={fetchNextChatsPage}
                hasNextPage={hasNextChatsPage}
                isFetchingNextPage={isFetchingNextChatsPage}
                streams={streams}
                owners={owners}
                messageCategories={messageCategories}
                conversationSignalOptions={conversationSignalOptions}
                expandedFilters={expandedFilters}
                onExpandedFiltersChange={setExpandedFilters}
                activeTab={activeTab}
                onTabChange={handleTabChange}
              />
            )}
            {view === 'conversation' && (
              <ConversationView
                selectedChat={selectedChat}
                onScheduleMessage={handleScheduleMessage}
                onMarkImportant={handleMarkImportant}
                onMarkArchived={handleMarkArchived}
                onMarkUnread={handleMarkUnread}
                onNavigateToDetails={handleNavigateToDetails}
                onSendEmailNotification={handleSendEmailNotification}
                isAdmin={isAdmin}
                owners={owners}
                onChatUpdate={(updatedChat) => {
                  setSelectedChat(updatedChat);
                  queryClient.invalidateQueries({ queryKey: ['chats'] });
                }}
                onSendScheduledNowSuccess={() => {
                  setToast({ open: true, message: 'Message sent!', severity: 'success' });
                }}
                onSendScheduledNowError={() => {
                  setToast({ open: true, message: 'Failed to send message', severity: 'error' });
                }}
              />
            )}
            {view === 'details' && (
              <ContactDetails selectedChat={selectedChat} onBack={handleBack} />
            )}
          </Box>
        )}
      </Box>
      
      {/* Toast Notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast(prev => ({ ...prev, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* AI Helper - floating button when a chat is selected */}
      <AiHelperFloatingButton
        visible={!!selectedChat}
        context={
          selectedChat
            ? {
                chatUuid: selectedChat.key,
                prospectName: selectedChat.prospect?.full_name ?? undefined,
              }
            : null
        }
      />

      {/* Email Notification Form */}
      <EmailNotificationForm
        open={emailNotificationOpen}
        onClose={handleCloseEmailNotification}
        chat={selectedChat}
        currentUser={{
          name: user?.name || '',
          email: user?.email || '',
        }}
        onSuccess={(msg?: string) => {
          setToast({ open: true, message: msg || 'Email notification sent successfully', severity: 'success' });
        }}
      />
    </Box>
  );
}; 